/**
 * Practice "eVitals Monitoring" flag (practices.evitals_monitoring).
 * Yes → eVitals monitoring (system caregiver required on patients)
 * No  → Self monitoring (system caregiver hidden / not required)
 */
export function isEvitalsMonitoringPractice(practiceOrValue) {
  const raw =
    typeof practiceOrValue === 'string' || typeof practiceOrValue === 'number'
      ? practiceOrValue
      : practiceOrValue?.evitals_monitoring ?? practiceOrValue?.evitalsMonitoring;

  // DB / form default is Yes when unset
  if (raw == null || String(raw).trim() === '') return true;

  const v = String(raw).trim().toLowerCase();
  if (v === 'no' || v === '0' || v === 'false' || v.startsWith('no')) return false;
  return v === 'yes' || v === '1' || v === 'true' || v.startsWith('yes');
}

export const MEASURED_TYPE_IDS = { BP: 1, BG: 2, WT: 3 };

export const PRACTICE_VITAL_ADD_DISABLED_TITLE =
  'This vital is no longer enabled for the practice. Existing readings remain view-only.';

export const PRACTICE_VITAL_SCHEDULE_DISABLED_TITLE =
  'This vital is no longer enabled for the practice. Schedule and target settings are view-only.';

/** Practice `selected_monitoring_types` may be number[], string[], or a JSON string. */
export function normalizePracticeMonitoringTypes(raw) {
  if (raw == null || raw === '') return [];
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      arr = raw.split(',').map((s) => s.trim());
    }
  }
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((x) => parseInt(String(x), 10)).filter((n) => !Number.isNaN(n) && n > 0))];
}

/**
 * Whether the practice currently allows adding new readings for a measured type.
 * `null` / omitted types means unknown (do not block). An array means enforce it.
 */
export function practiceAllowsMeasuredType(types, typeId) {
  if (!Array.isArray(types)) return true;
  return types.map(Number).includes(Number(typeId));
}

export function canAddPracticeVital({ practiceIsLocked = false, practiceMonitoringTypes, typeId } = {}) {
  if (practiceIsLocked) return false;
  return practiceAllowsMeasuredType(practiceMonitoringTypes, typeId);
}

/** Schedule/target sub-tabs that this practice is enrolled to configure. */
export function getPracticeMonitoringSectionFlags(raw) {
  const types = normalizePracticeMonitoringTypes(raw);
  return {
    bp: types.includes(MEASURED_TYPE_IDS.BP),
    bg: types.includes(MEASURED_TYPE_IDS.BG),
    weight: types.includes(MEASURED_TYPE_IDS.WT),
  };
}
